import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import DemoNotice from './components/DemoNotice';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import LoadingFallback from './components/LoadingFallback';
import LoadingSpinner from './components/LoadingSpinner';
import ScrollToTop from './components/ScrollToTop';

// Lazy load heavy components
const LazyDonorRegistration = React.lazy(() => import('./pages/DonorRegistration'));
const LazyRecipientDashboard = React.lazy(() => import('./pages/RecipientDashboard'));
const LazyHospitalPortal = React.lazy(() => import('./pages/HospitalPortal'));
const LazyEducationalHub = React.lazy(() => import('./pages/EducationalHub'));
const LazyAboutPage = React.lazy(() => import('./pages/AboutPage'));
const LazyAccountSettings = React.lazy(() => import('./pages/AccountSettings'));
const LazyEditProfile = React.lazy(() => import('./pages/EditProfile'));

const AppContent: React.FC = () => {
  const { user, profile, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Scroll to top component */}
      <ScrollToTop />
      
      {/* Fixed Header */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <Header />
      </div>
      
      {/* Main Content with top padding to account for fixed header */}
      <main className="flex-1 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <DemoNotice />
        </div>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<LazyAboutPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register-donor" element={<LazyDonorRegistration />} />
            <Route path="/education" element={<LazyEducationalHub />} />
            <Route 
              path="/recipient-dashboard" 
              element={
                user && profile?.role === 'recipient' ? 
                  <LazyRecipientDashboard /> : 
                  <Navigate to="/login" />
              } 
            />
            <Route 
              path="/hospital-portal" 
              element={
                user && profile?.role === 'hospital' ? 
                  <LazyHospitalPortal /> : 
                  <Navigate to="/login" />
              } 
            />
            <Route 
              path="/account-settings" 
              element={
                user ? 
                  <LazyAccountSettings /> : 
                  <Navigate to="/login" />
              } 
            />
            <Route 
              path="/edit-profile" 
              element={
                user ? 
                  <LazyEditProfile /> : 
                  <Navigate to="/login" />
              } 
            />
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;